import React, { useState } from "react";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { SimpleForm } from "@/shared/components/text-box/simple-form";
import { PasswordForm } from "@/shared/components/text-box/password-form";
import { motion } from "framer-motion";
import type { ClientInfo } from "@/features/accounts/account-group/hooks/use-kcloud-integration";
import { Checkbox } from "@/shared/components/check-box/check-box";
import { useKCloudLogin } from "@/features/accounts/account-group/hooks/use-kcloud-login";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";

interface Props {
  serviceType: "kaikei" | "shokokai"; // 1: 会計事務所, 2: 商工会
  onTapCloseModal: () => void;
  onConnect: (selectedClient: ClientInfo) => Promise<void>;
  onAuthenticate: (kikCode: string, userId: string, password: string) => Promise<ClientInfo[]>;
}

export const ConnectKCloudModal = ({ 
  serviceType, 
  onTapCloseModal, 
  onConnect,
  onAuthenticate
}: Props) => {
  const [isExiting, setIsExiting] = useState(false);

  const serviceTitle =
    serviceType === "kaikei"
      ? "会計事務所クラウド"
      : "商工会クラウド";

  const serviceCodeLabel =
    serviceType === "kaikei"
      ? "会計事務所コード"
      : "商工会コード";


  const {
    kikCode,
    userId,
    password,
    isConnecting,
    clientList,
    selectedClient,
    showAllClients,
    filteredClientList,

    setKikCode,
    setUserId,
    setPassword,
    setSelectedClient,

    handleAuthenticate,
    handleConnect,
    handleShowAllClientsChange,
  } = useKCloudLogin({
    onAuthenticate,
    onConnect,
    onClose: onTapCloseModal,
  });

  const {
    targetAccountGroup,
  } = useAccountGroupsContext();

  const isCredentialLocked = clientList.length > 0;


  return (
    <>
      <div key="modalOverlay" id="modalOverlay" className="overlay show"></div>
      <motion.aside
        key="modalAside"
        id="modal"
        className="modal-container show"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: isExiting ? 0 : 1, y: isExiting ? 30 : 0 }}
        transition={{ duration: 0.2 }}
        onAnimationComplete={() => {
          if (isExiting) onTapCloseModal();
        }}
      >
        <BaseRoundButton id="closeModal" className="btn-close" onClick={() => {setIsExiting(true);}} loadingNegative={true} />
        <div className="modal-content fade-in">
          <header>
            <h1>サービス連携</h1>
          </header>
          <input 
            type="checkbox" 
            id="confirmId" 
            checked={clientList.length > 0}
            readOnly
          />
          <section className="md-user-service">
            <h2>{serviceTitle}との連携</h2>
            <div className="group-name md-bank-style">
              <div className="label">グループ名</div>
              <div className="value">{targetAccountGroup.accountGroupsName}</div>
            </div>
            <dl>
              <dt>
                <label>{serviceCodeLabel}</label>
              </dt>
              <dd>
                <SimpleForm
                  initialValue={kikCode}
                  placeholder={`${serviceCodeLabel}を入力`}
                  onChange={(value) => setKikCode(value)}
                  validationRule="numericOnly"
                  maxLength={9}
                  isReadOnly={isCredentialLocked}
                />
              </dd>
              <dt>
                <label>ユーザーID</label>
              </dt>
              <dd>
                <SimpleForm
                  title=""
                  initialValue={userId}
                  placeholder="ユーザーIDを入力"
                  onChange={(value) => setUserId(value)}
                  validationRule=""
                  maxLength={10}
                  isReadOnly={isCredentialLocked}
                />
              </dd>

              <dt>
                <label>パスワード</label>
              </dt>
              <dd>
                <PasswordForm
                  initialValue={password}
                  title=""
                  placeholder="パスワードを入力"
                  onChange={(value) => setPassword(value)}
                  labelable={false}
                  validationRule="halfWidthAlnumSymbols"
                  maxLength={10}
                  isReadOnly={isCredentialLocked}
                />
              </dd>
            </dl>
            
            <label 
              id="btnConfirmId"
              htmlFor="confirmId"
              onClick={handleAuthenticate}
            >
              {isConnecting ? "認証中..." : "ログイン認証"}
            </label>

            {clientList.length > 0 ? (
              <>
                <div className="showAllClientsWrapper">
                  <Checkbox
                    label={"担当外の事業所も表示する"}
                    checked={showAllClients}
                    handleChange={handleShowAllClientsChange}
                    className="showAllClients"
                    id="chkShowAll"
                  />
                </div>
                <dl id="selectOffice">
                  <dt>連携先</dt>
                  {filteredClientList.length > 0 ? (
                    <dd>
                      <ul>
                        {filteredClientList.map((client) => (
                          <li key={client.uniClientCode}>
                            <input 
                              type="radio" 
                              id={`office_${client.uniClientCode}`} 
                              name="selectOffice" 
                              checked={selectedClient === client} 
                              onChange={() => setSelectedClient(client)} 
                            />
                            <label htmlFor={`office_${client.uniClientCode}`}>
                              <strong>{client.clientName}</strong>
                              <span>({client.clientCode})</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  ) : (
                    <dd><p>表示可能な事業所がありません。</p></dd>
                  )}
                </dl>
              </>
            ) : <></>}
          </section>
          {clientList.length > 0 && (
            <nav id="btnSaveJoint">
              <BaseRoundButton
                buttonText={isConnecting ? "連携中..." : "この内容で連携する"}
                isActive={selectedClient !== null && !isConnecting}
                onClick={handleConnect}
              />
            </nav>
          )}
        </div>
      </motion.aside>
    </>
  );
};
